/**
 * Product flavour selector (shared)
 *
 * Generic, family-agnostic swatch picker for the consolidated product
 * templates (Electrolytes, Creatine, Clear Whey Protein). Pairs with
 * snippets/product-flavour-selector.liquid, which renders one swatch per
 * real Shopify variant plus a JSON payload of each variant's gallery
 * images (sourced from a variant metafield at render time) — this script
 * never fetches another product's data, so it keeps working after the
 * superseded single-flavour products are archived.
 *
 * On click it:
 *  - updates the product form's hidden `id` input and fires `change`
 *  - dispatches `variantChanged` / `gp:variant-change` so both GemPages and
 *    skio-plan-picker-variants-widget.js pick up the new variant
 *  - swaps the gallery synchronously from the pre-rendered image map
 *  - mirrors the selection into the URL's `?variant=` param
 *
 * On load it reads `?variant=` (or the form's current variant id) and
 * selects synchronously, so there is no default-flavour flash while a
 * network request resolves.
 */
(() => {
  const initPicker = (root) => {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    const swatches = [...root.querySelectorAll(".product-flavour-swatch")];
    if (!swatches.length) return;
    const selectedFlavour = root.querySelector("[data-selected-flavour]");

    const dataScript = root.querySelector('[data-flavour-gallery-data]');
    let galleriesByVariantId = {};
    try {
      galleriesByVariantId = dataScript ? JSON.parse(dataScript.textContent) : {};
    } catch (error) {
      console.warn("[product-flavour-selector] Could not parse gallery data.", error);
    }

    const findProductScope = () =>
      root.closest("gp-product") ||
      root.closest(".gp-product-wrapper") ||
      document.querySelector("gp-product");

    const findForm = (productScope) =>
      root.closest('form[action*="/cart/add"]') ||
      productScope?.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[data-type="add-to-cart-form"]');

    const updateJsonAttribute = (element, attribute, property, value) => {
      if (!element?.hasAttribute(attribute)) return;
      try {
        const data = JSON.parse(element.getAttribute(attribute));
        data[property] = value;
        element.setAttribute(attribute, JSON.stringify(data));
      } catch (error) {
        console.warn(`[product-flavour-selector] Could not update ${attribute}.`, error);
      }
    };

    const updateSelectedSwatch = (variantId) => {
      swatches.forEach((swatch) => {
        const selected = String(swatch.dataset.variantId) === String(variantId);
        swatch.classList.toggle("active", selected);
        swatch.setAttribute("aria-checked", String(selected));
        if (selected && selectedFlavour) {
          selectedFlavour.textContent = swatch.getAttribute("aria-label") || "";
        }
      });
    };

    const getGallery = () => {
      const productScope = findProductScope();
      const existing = productScope?.querySelector(".product-flavour-gallery");
      if (existing) return existing;

      const gemPagesGallery = productScope?.querySelector("gp-product-images-v3");
      if (!gemPagesGallery) return null;

      const gallery = document.createElement("div");
      gallery.className = "product-flavour-gallery";
      gallery.innerHTML = `
        <div class="product-flavour-gallery-main">
          <button type="button" class="product-flavour-gallery-arrow" data-direction="previous" aria-label="Previous image">‹</button>
          <img src="" alt="" data-flavour-gallery-main-image>
          <button type="button" class="product-flavour-gallery-arrow" data-direction="next" aria-label="Next image">›</button>
        </div>
        <div class="product-flavour-gallery-thumbnails" data-flavour-gallery-thumbnails></div>
      `;

      gemPagesGallery.insertAdjacentElement("beforebegin", gallery);
      gallery._gemPagesGallery = gemPagesGallery;
      gemPagesGallery.style.display = "none";

      return gallery;
    };

    const renderGallery = (variantId, label) => {
      const images = galleriesByVariantId[variantId];
      const gallery = getGallery();
      if (!gallery) return;

      if (!images || !images.length) {
        gallery.style.display = "none";
        if (gallery._gemPagesGallery) gallery._gemPagesGallery.style.display = "";
        return;
      }

      gallery.style.display = "";
      if (gallery._gemPagesGallery) gallery._gemPagesGallery.style.display = "none";

      const mainImage = gallery.querySelector("[data-flavour-gallery-main-image]");
      const thumbnails = gallery.querySelector("[data-flavour-gallery-thumbnails]");
      const previousButton = gallery.querySelector('[data-direction="previous"]');
      const nextButton = gallery.querySelector('[data-direction="next"]');

      let activeIndex = 0;

      const showImage = (index) => {
        activeIndex = (index + images.length) % images.length;
        mainImage.src = images[activeIndex];
        mainImage.alt = `${label} product image ${activeIndex + 1}`;

        thumbnails.querySelectorAll(".product-flavour-gallery-thumbnail").forEach((thumbnail, thumbnailIndex) => {
          thumbnail.classList.toggle("active", thumbnailIndex === activeIndex);
        });
      };

      thumbnails.innerHTML = "";
      images.forEach((image, index) => {
        const thumbnail = document.createElement("button");
        thumbnail.type = "button";
        thumbnail.className = "product-flavour-gallery-thumbnail";
        thumbnail.setAttribute("aria-label", `View image ${index + 1}`);

        const thumbnailImage = document.createElement("img");
        thumbnailImage.src = image;
        thumbnailImage.alt = "";
        thumbnailImage.loading = index < 4 ? "eager" : "lazy";

        thumbnail.appendChild(thumbnailImage);
        thumbnail.addEventListener("click", () => showImage(index));
        thumbnails.appendChild(thumbnail);
      });

      previousButton.onclick = () => showImage(activeIndex - 1);
      nextButton.onclick = () => showImage(activeIndex + 1);
      previousButton.hidden = images.length < 2;
      nextButton.hidden = images.length < 2;

      // Re-published every render (like the button onclick handlers above) so
      // the swipe listener — bound once, below — always steps the current
      // flavour's gallery instead of a stale closure from an earlier one.
      gallery._showPrevious = () => showImage(activeIndex - 1);
      gallery._showNext = () => showImage(activeIndex + 1);

      if (!gallery.dataset.swipeBound) {
        gallery.dataset.swipeBound = "true";
        const main = gallery.querySelector(".product-flavour-gallery-main");
        let touchStartX = 0;

        main.addEventListener("touchstart", (event) => {
          touchStartX = event.changedTouches[0].clientX;
        }, { passive: true });

        main.addEventListener("touchend", (event) => {
          const deltaX = event.changedTouches[0].clientX - touchStartX;
          const swipeThreshold = 40;
          if (deltaX > swipeThreshold) gallery._showPrevious();
          else if (deltaX < -swipeThreshold) gallery._showNext();
        }, { passive: true });
      }

      showImage(0);
    };

    const selectSwatch = (swatch, { updateUrl = true } = {}) => {
      const variantId = swatch.dataset.variantId;
      const form = findForm(findProductScope());
      const variantInput = form?.querySelector('input[name="id"]');
      if (!variantId || !variantInput) return;

      updateSelectedSwatch(variantId);

      variantInput.value = variantId;
      variantInput.setAttribute("value", variantId);
      variantInput.dispatchEvent(new Event("input", { bubbles: true }));
      variantInput.dispatchEvent(new Event("change", { bubbles: true }));

      const productScope = findProductScope();
      const gpButton = productScope?.querySelector("gp-product-button");
      updateJsonAttribute(gpButton, "gp-data", "variantID", variantId);
      updateJsonAttribute(gpButton, "gp-data", "inStock", swatch.dataset.available === "true");
      if (gpButton) gpButton.variantID = variantId;

      document.dispatchEvent(
        new CustomEvent("variantChanged", {
          detail: { variantId: Number(variantId) },
        })
      );

      productScope?.dispatchEvent(
        new CustomEvent("gp:variant-change", {
          bubbles: true,
          detail: { variantId: Number(variantId) },
        })
      );

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variantId);
        window.history.replaceState({}, "", url);
      }

      // A single flavour keeps the full GemPages product gallery.
      if (swatches.length > 1) {
        renderGallery(variantId, swatch.getAttribute("aria-label") || "");
      }
    };

    swatches.forEach((swatch) => {
      swatch.addEventListener("click", () => selectSwatch(swatch));
    });

    const urlVariantId = new URL(window.location.href).searchParams.get("variant");
    const form = findForm(findProductScope());
    const currentVariantId = urlVariantId || form?.querySelector('input[name="id"]')?.value;

    const initialSwatch =
      swatches.find((swatch) => String(swatch.dataset.variantId) === String(currentVariantId)) ||
      swatches.find((swatch) => !swatch.hasAttribute("disabled"));

    if (initialSwatch) selectSwatch(initialSwatch, { updateUrl: false });
  };

  const init = () => {
    document.querySelectorAll("[data-product-flavour-selector]").forEach(initPicker);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
